/**
 * @file Button 基础按钮
 * @description 管理后台紧凑按钮，统一 h-8（32px）；无 size API。
 *
 * @whenToUse 表单提交、工具栏、对话框确认/取消等点击操作。
 * @whenNotToUse
 * 1. 右下角全局悬浮入口（用 FloatButton）。
 * 2. 纯文字导航链接（用 Link + text 样式）。
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** 视觉变体 */
  variant?: ButtonVariant
  /** 加载中（自动 disabled） */
  loading?: boolean
  /** 前置图标 */
  icon?: ReactNode
}

const variantClass: Record<ButtonVariant, string> = {
  primary:
    'border-primary bg-primary text-primary-foreground hover:opacity-90 focus-visible:opacity-90',
  secondary:
    'border-border bg-raised text-foreground hover:opacity-90 focus-visible:opacity-90',
  ghost:
    'border-transparent bg-transparent h-fit p-0 text-foreground hover:opacity-90 focus-visible:opacity-90',
  danger:
    'border-danger bg-danger text-raised hover:opacity-90 focus-visible:opacity-90',
  outline:
    'border-border bg-transparent text-foreground hover:opacity-90 focus-visible:opacity-90',
}

/**
 * 紧凑按钮（固定 h-8 / 32px）
 */
export function Button({
  variant = 'primary',
  loading = false,
  icon,
  className,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
        variantClass[variant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
      ) : (
        icon
      )}
      {children}
    </button>
  )
}
